import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { LOOK_SAMPLE_SEC } from 'fym-shared/src/music24/releaseKitLookSamples';
import { TypedMemo } from '../../../../common/types';
import { themeSampleUrls } from '../../themes/themeStillVars';
import { usePrefersReducedMotion } from '../../themes/usePrefersReducedMotion';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The length badge of a playing sample, "0:04". A number, so it needs no translation. */
const SAMPLE_LENGTH_LABEL = `0:${String(LOOK_SAMPLE_SEC).padStart(2, '0')}`;

interface ThemeSampleProps {
  themeId: ReleaseThemeId;
  /** The tile is hovered, focused or (with no other tile active) selected: the loop plays. */
  playing: boolean;
}

/**
 * The sample of one theme (design q2b): the poster when idle, the muted 4 s loop of the demo song
 * Swamps while it plays. A new mount of the video starts the loop from its start. With reduced
 * motion there is no video element: the poster only, with no length badge and no progress line.
 * A poster that does not load leaves the skeleton; the tile still selects.
 */
const ThemeSampleComponent: React.FC<ThemeSampleProps> = ({ themeId, playing }) => {
  const reducedMotion = usePrefersReducedMotion();
  const [posterState, setPosterState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const urls = themeSampleUrls(themeId);
  const showVideo = playing && !reducedMotion;
  const onPosterLoad = useCallback(() => setPosterState('ready'), []);
  const onPosterError = useCallback(() => setPosterState('failed'), []);

  return (
    <span
      className={clsx(styles.sample, posterState !== 'ready' && styles.sampleLoading)}
      data-testid={`theme-sample-${themeId}`}
    >
      {posterState !== 'failed' && (
        <img
          className={styles.sampleMedia}
          src={urls.poster}
          alt=""
          loading="lazy"
          onLoad={onPosterLoad}
          onError={onPosterError}
          data-testid="theme-sample-poster"
        />
      )}
      {showVideo && (
        <video
          key={urls.video}
          className={styles.sampleMedia}
          src={urls.video}
          poster={urls.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          data-testid="theme-sample-video"
        />
      )}
      {showVideo && (
        <>
          <span className={styles.sampleLength}>{SAMPLE_LENGTH_LABEL}</span>
          <span className={styles.sampleProgress}>
            <i />
          </span>
        </>
      )}
    </span>
  );
};

export const ThemeSample = TypedMemo(ThemeSampleComponent);
